import { observable, action, computed, makeObservable } from 'mobx';
import BuildingStore from './BuildingStore';
import agent from '../lib/agent';
import { formatBuildingPower } from '../lib/formatData';
import translations from '../constants/translations';

class Store {
  isLoading = false;
  id = null;
  building = null;
  estimates = null;
  estimates_int = null;
  financialsHash = null;
  details = null;
  weather_timestamp = null;
  wind_speed = null;
  temperature = null;
  places = null;
  financials = null;
  closest_power_plant = null;
  power_line = null;
  weatherData = [];
  predictions = [];
  centroid = null;
  bbox = null;
  timeOfDay = new Date(1681488000000);
  financialMode = 'default';
  showUploadedData = false;

  setFinancialsHash = hash => {
    this.financialsHash = hash;
  };

  setFinancials = financials => {
    this.financials = financials;
  };

  loadBuilding = async buildingId => {
    if (this.id === buildingId && !this.isLoading) {
      return true;
    }
    this.building = null;
    this.isLoading = true;
    this.centroid = null;
    this.bbox = null;
    this.financials = null;
    this.financialMode = 'default';
    this.timeOfDay = new Date(1681488000000);
    const building = await BuildingStore.loadBuilding(buildingId);

    if (building) {
      this.id = building.id;
      this.details = building.details;
      this.estimates = building.estimates;
      this.estimates_int = building.estimates_int;

      this.building = building;
      this.centroid = building.centroid;
      this.bbox = building.bbox;
      this.weather_timestamp = null;
      this.wind_speed = null;
      this.temperature = null;
      this.weatherData = [];
      this.predictions = [];
      this.places = null;
      this.closest_power_plant = null;
      this.power_line = null;
      this.isLoading = false;

      if (building.financials) {
        this.setFinancials(building.financials);
      }
      return building;
    }
    this.isLoading = false;
    return null;
  };

  loadFinancials = async () => {
    const building = await BuildingStore.loadBuilding(this.id);

    if (building) {
      console.log('new financials');
      this.setFinancials(building.financials);
      return building;
    }
    return null;
  };

  constructor() {
    makeObservable(this, {
      isLoading: observable,
      showUploadedData: observable,
      id: observable,
      centroid: observable,
      financialsHash: observable,
      estimates: observable,
      estimates_int: observable,
      timeOfDay: observable,
      details: observable,
      weather_timestamp: observable,
      financials: observable,
      wind_speed: observable,
      temperature: observable,
      places: observable,
      closest_power_plant: observable,
      power_line: observable,
      weatherData: observable,
      predictions: observable,
      loadBuilding: action,
      financialMode: observable,
      sections: computed,
      quickMenuLinks: computed,
      getWeather: action,
      getPredictions: action,
      getPlaces: action,
      setFinancialMode: action,
      getPowerOptions: action
    });
  }

  get sections() {
    if (this.details) {
      return Object.keys(this.details)
        .filter(key => this.details[key])
        .map(id => ({
          id,
          title: translations[id]
        }));
    }
    return [];
  }

  get analyticsUrl() {
    if (this.financials) {
      return this.financials.analytics_id;
    }
    return null;
  }

  get emissionsData() {
    if (this.financials) {
      return this.financials.emissions;
    }
    return null;
  }

  get cashFlow() {
    if (this.financials) {
      return this.financials.capex_cash_flow;
    }
    return null;
  }

  get cashFlowPPA() {
    if (this.financials) {
      return this.financials.financials_ppa.capex_cash_flow;
    }

    return null;
  }

  get formattedFinancials() {
    if (this.financials?.formatted_financials) {
      return this.financials.formatted_financials.financials;
    }
    return [];
  }

  get formattedFinancialsPPA() {
    if (this.financials?.formatted_financials_ppa) {
      return this.financials.formatted_financials_ppa.financials;
    }
    return [];
  }

  get quickMenuLinks() {
    if (this.building) {
      const { contacts, tariff } = this.building;
      const links = [...this.sections];
      if (tariff) {
        links.push({
          id: 'tariff',
          title: `${translations.tariff}: ${tariff.name}`
        });
      }
      links.push({
        id: 'places',
        title: translations.places
      });
      if (contacts && !!contacts.length) {
        links.push({
          id: 'contacts',
          title: translations.contacts
        });
      }

      return links;
    }
    return [];
  }

  getBuildingFinancials = async projectId => {
    const { data } = await agent.Project.getBuildingFinancials(
      projectId,
      this.id
    );

    this.financials = data.data;
  };

  getWeather = async (lon, lat) => {
    const { data } = await agent.Building.getWeather(lon, lat);
    if (data.data) {
      this.weatherData = data.data.charts;
    }
  };

  getPredictions = async buildingId => {
    if (this.building.project_id) {
      const { data } = await agent.Project.getProjectBuildingPredictions(
        this.building.project_id,
        buildingId
      );
      this.predictions = data.data;
    } else {
      const { data } = await agent.Building.getPredictions(buildingId);
      this.predictions = data.data;
    }
  };

  getPlaces = async (buildingId, lon, lat) => {
    const { data } = await agent.Building.getPlaces(lon, lat);
    if (data?.data) {
      this.places = data.data;
    }
  };

  getPowerOptions = async (buildingId, lon, lat) => {
    const { data } = await agent.Building.getPowerOptions(lon, lat);
    if (data?.data) {
      const {
        power_line_closest_point,
        power_line_geometry,
        power_line_id,
        power_plant_geometry
      } = data.data;

      this.details = {
        ...this.details,
        ...formatBuildingPower(data.data)
      };
      this.closest_power_plant = true;
      this.power_line = {
        power_line_closest_point,
        power_line_geometry,
        power_line_id,
        power_plant_geometry
      };
    }
  };

  setEstimates = (estimates, estimates_int) => {
    if (!estimates) {
      throw new Error('estimates is required');
    }

    if (!estimates_int) {
      throw new Error('estimates_int is required');
    }

    this.estimates = estimates;
    this.estimates_int = estimates_int;
  };

  setFinancialMode = mode => {
    this.financialMode = mode;
  };

  setTimeOfDay = time => {
    this.timeOfDay = time;
  };

  setShowUploadedData = show => {
    this.showUploadedData = show;
  };
}

const store = new Store();
export default store;
